import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { ScheduleStrategySummary } from '../../types/schedule';
import { Modal } from '../ui/Modal';
import PnlFlash from '../common/PnlFlash';
import { formatTimeRange, formatTradingType } from '../../utils/scheduleSummary';
import { StatusBoxRow } from '../common/StatusBoxRow';
import { expiryTypeLabel } from '../../utils/strategy/expiryDisplay';
import { BasketModalHeader } from './shared/BasketModalHeader';
import { StrategyActionToolbar } from './shared/StrategyActionToolbar';
import BrokerBreakdownLine from './shared/BrokerBreakdownLine';

interface BasketScheduleModalProps {
  basketId: string;
  basketName: string;
  summaries: ScheduleStrategySummary[];   // already filtered to this basket
  onClose: () => void;
  onOpenStrategy: (groupKey: string) => void;
  /** When provided, renders an "Exit All" button in the toolbar that exits every open position in the basket. */
  onExitBasket?: (basketId: string) => void;
  /** When provided, renders a "Retry All" button in the toolbar that retries every rejected entry across the basket's strategies. */
  onRetryBasket?: (basketId: string) => void;
  /** When provided, renders a "Terminate" button that platform-only stop-watches every non-terminal position in the basket (never calls the broker). */
  onTerminateBasket?: (basketId: string) => void;
  /** Disables the bulk action buttons and shows a spinner while an in-flight action is running. */
  actionPending?: boolean;
  /** When provided, renders a refresh icon in the header that re-fetches the schedule summary. */
  onRefresh?: () => void;
  /** Shows a spinning refresh indicator when true. */
  refreshing?: boolean;
}

function keyOf(s: ScheduleStrategySummary): string {
  return `${s.basket_id}#${s.strategy_id}`;
}

/** Basket-level modal — lists every strategy in this basket for the day. */
const BasketScheduleModal: React.FC<BasketScheduleModalProps> = ({
  basketId,
  basketName,
  summaries,
  onClose,
  onOpenStrategy,
  onExitBasket,
  onRetryBasket,
  onTerminateBasket,
  actionPending = false,
  onRefresh,
  refreshing = false,
}) => {
  // Aggregate basket-level stats from summaries.
  let legTotal = 0;
  let filledTotal = 0, pendingTotal = 0;
  // Only broker rejections drive the fill-progress ribbon's red variant and
  // the Retry All toolbar. Cancellations are tracked separately as an
  // informational annotation — they are not errors to act on.
  let rejectedTotal = 0, cancelledTotal = 0;
  let openTotal = 0, closedTotal = 0;
  let realized = 0, unrealized = 0;
  let slPlacedTotal = 0, slPendingTotal = 0, slRejectedTotal = 0;
  const underlyingSet = new Set<string>();
  const tradingTypeSet = new Set<string>();
  for (const s of summaries) {
    legTotal     += s.leg_count;
    filledTotal  += s.entry_counts.complete;
    pendingTotal += s.entry_counts.pending + s.entry_counts.open;
    rejectedTotal += s.entry_counts.rejected;
    cancelledTotal += s.entry_counts.cancelled;
    openTotal    += s.open_count;
    closedTotal  += s.closed_count;
    realized     += s.realized_pnl;
    unrealized   += s.unrealized_pnl;
    slPlacedTotal   += s.sl_counts.placed;
    slPendingTotal  += s.sl_counts.pending;
    slRejectedTotal += s.sl_counts.rejected;
    if (s.underlying) underlyingSet.add(s.underlying);
    if (s.trading_type) tradingTypeSet.add(s.trading_type);
  }
  const retryableTotal = rejectedTotal;

  const pills: string[] = [];
  underlyingSet.forEach((u) => pills.push(u));
  tradingTypeSet.forEach((t) => pills.push(t));

  const sorted = React.useMemo(() => [...summaries].sort((a, b) => {
    const at = a.scheduled_entry_time || a.scheduled_exit_time || '99:99';
    const bt = b.scheduled_entry_time || b.scheduled_exit_time || '99:99';
    if (at !== bt) return at.localeCompare(bt);
    return a.strategy_name.localeCompare(b.strategy_name);
  }), [summaries]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      maxWidth="6xl"
      ariaLabel={`${basketName} schedule`}
    >
        <BasketModalHeader
          basketName={basketName}
          strategyCount={sorted.length}
          legTotal={legTotal}
          pills={pills}
          filled={filledTotal}
          pending={pendingTotal}
          rejected={rejectedTotal}
          cancelled={cancelledTotal}
          openCount={openTotal}
          closedCount={closedTotal}
          realized={realized}
          unrealized={unrealized}
          slPlaced={slPlacedTotal}
          slPending={slPendingTotal}
          slRejected={slRejectedTotal}
          onClose={onClose}
          onRefresh={onRefresh}
          refreshing={refreshing}
        />

        <StrategyActionToolbar
          retryableCount={retryableTotal}
          exitableCount={openTotal}
          onRetryAll={onRetryBasket
            ? () => onRetryBasket(basketId)
            : undefined}
          onExitAll={onExitBasket
            ? () => onExitBasket(basketId)
            : undefined}
          onTerminateAll={onTerminateBasket
            ? () => onTerminateBasket(basketId)
            : undefined}
          pending={actionPending}
        />

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {sorted.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No strategies scheduled for this basket on this day.
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {['Time', 'Strategy', 'Underlying', 'Legs', 'Health', 'P&L', 'Broker', ''].map((h, i) => (
                    <th
                      key={h || `h-${i}`}
                      className="px-3 py-2.5 text-left text-[10px] font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => {
                  const time = formatTimeRange(
                    s.scheduled_entry_time,
                    s.scheduled_exit_time,
                    s.intraday_exit_mode,
                  );
                  const brokerLabel = s.brokers.length === 0 ? '' : s.brokers.length === 1 ? s.brokers[0] : 'Multiple';
                  return (
                    <tr
                      key={keyOf(s)}
                      className="border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                      onClick={() => onOpenStrategy(keyOf(s))}
                    >
                      <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap">{time}</td>
                      <td className="px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 hover:underline hover:text-blue-600 dark:hover:text-blue-400">
                          {s.strategy_name || '—'}
                          <ChevronRight className="w-3 h-3 opacity-60" />
                        </span>
                        {s.trading_type && (
                          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-0.5">
                            {formatTradingType(s.trading_type, s.intraday_exit_mode)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        {s.underlying}
                        <span className="ml-1 text-[10px] uppercase text-gray-400 dark:text-gray-500 whitespace-nowrap">
                          {expiryTypeLabel(s.expiry_type)}
                          {s.product_type && ` · ${s.product_type}`}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-700 dark:text-gray-200 tabular-nums whitespace-nowrap">
                        <div className="flex flex-col gap-0.5">
                          <span>
                            {s.leg_count} {s.leg_count === 1 ? 'leg' : 'legs'}
                            <span className="ml-1 text-gray-500 dark:text-gray-400">· {s.total_effective_lots ?? s.total_lots}L</span>
                          </span>
                          <BrokerBreakdownLine items={s.broker_breakdown ?? []} />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><StatusBoxRow summary={s} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <PnlFlash value={s.total_pnl} className="text-xs font-medium" />
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {brokerLabel || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right">
                        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
    </Modal>
  );
};

export default BasketScheduleModal;
